import { type EditorModelWithoutRelations } from '@/lib/types';
import { useContext, useState } from 'react';
import ModelsContext from '../ModelsContext';
import Prompt from '@/components/Prompt';
import Dialog from '@/components/Dialog';
import useApi from '@/lib/api/useApi';
import {
  CircularProgress,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import type ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import { AddCircleOutlineOutlined as AddIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';

const CreateButton = styled(ListItemButton)({
  '.MuiListItemText-root': {
    height: 24,
    display: 'flex',
    alignItems: 'center',
    '.MuiListItemText-primary': {
      fontWeight: 500,
      fontSize: '0.875rem',
      textTransform: 'uppercase',
    },
  },
});

type CreateModelProps = {
  selectedModelType: ModelType;
  dialogOpen: boolean;
  onOpenDialog: () => void;
  onCloseDialog: () => void;
};

const CreateModel = ({
  selectedModelType,
  dialogOpen,
  onOpenDialog,
  onCloseDialog,
}: CreateModelProps) => {
  const api = useApi();

  const [, setModels] = useContext(ModelsContext);
  const [saving, setSaving] = useState(false);

  const saveNewModel = async (name: string): Promise<void> => {
    onCloseDialog();
    setSaving(true);

    const model = await api.post<EditorModelWithoutRelations>(
      `editor/challenge-model/models`,
      {
        type: selectedModelType,
        name,
      },
    );

    setModels(models => {
      models[model.id] = {
        ...model,
        sections: {},
        fields: {},
      };
    });

    setSaving(false);
  };

  return (
    <>
      <CreateButton onClick={onOpenDialog} disabled={saving}>
        <ListItemIcon>
          {saving ? (
            <CircularProgress size={18} color="secondary" />
          ) : (
            <AddIcon />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <FormattedMessage id="editor.model_editor.model_selector.create_model.button" />
          }
        />
      </CreateButton>
      <Prompt
        open={dialogOpen}
        onCancel={onCloseDialog}
        onOk={saveNewModel}
        OkButton={Dialog.Actions.CreateButton}
        emptyError="prompt.errors.name_empty"
      >
        <Dialog.Title>
          <FormattedMessage id="editor.model_editor.model_selector.create_model.dialog.title" />
        </Dialog.Title>
        <Prompt.TextField
          label={
            <FormattedMessage id="editor.model_editor.model_selector.create_model.dialog.input_label" />
          }
        />
      </Prompt>
    </>
  );
};

export default CreateModel;
